import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	CardBody,
	InfoBanner,
	Input,
	Modal,
	Page,
	Table,
	type TableColumn,
	useToast,
} from "@theg1group/ui";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { ApiError, api } from "../lib/api.ts";
import { type Tone, fmtAgo, fmtUsd } from "../lib/format.ts";
import type {
	OverlapCell,
	OverlapLink,
	OverlapMatrix,
	OverlapPairData,
	OverlapPartner,
	OverlapSanction,
} from "../lib/types.ts";

const KIND_TONES: Record<string, Tone> = {
	// on-chain, strong "one operator" ties
	shared_wallet: "error",
	shared_deposit_address: "error",
	sanctioned_proximity: "error",
	direct_flow: "error",
	same_cluster: "warning",
	common_funder: "warning",
	common_counterparty: "info",
	// off-chain identity ties (registration metadata, no chain needed) — distinct amber/blue tone
	same_contact: "warning",
	same_website: "warning",
	same_email_domain: "info",
	same_name: "info",
};

/** Off-chain identity kinds render with a dotted underline so they read apart from on-chain ties. */
const OFFCHAIN_KINDS = new Set(["same_contact", "same_website", "same_email_domain", "same_name"]);

export function KindBadge({ kind }: { kind: string }) {
	const offchain = OFFCHAIN_KINDS.has(kind);
	return (
		<span title={offchain ? "off-chain identity match" : "on-chain link"}>
			<Badge
				size="sm"
				variant={KIND_TONES[kind] ?? "default"}
				className={offchain ? "border border-dotted" : undefined}
			>
				{kind.replaceAll("_", " ")}
			</Badge>
		</span>
	);
}

interface SelectedPair {
	a: OverlapPartner;
	b: OverlapPartner;
}

export function Overlap() {
	const qc = useQueryClient();
	const toast = useToast();
	const [minScore, setMinScore] = useState("20");
	const [pair, setPair] = useState<SelectedPair | null>(null);
	const refetchTimer = useRef<number | null>(null);

	useEffect(
		() => () => {
			if (refetchTimer.current) window.clearTimeout(refetchTimer.current);
		},
		[],
	);

	const matrix = useQuery({
		queryKey: ["overlap-matrix", minScore],
		queryFn: () =>
			api.get<OverlapMatrix>(`/api/overlap/matrix?minScore=${encodeURIComponent(minScore || "0")}`),
		retry: false,
	});

	const recompute = useMutation({
		mutationFn: () => api.post("/api/overlap/recompute", {}),
		onSuccess: () => {
			toast.addToast("success", "Recompute queued — refreshing in ~5s");
			if (refetchTimer.current) window.clearTimeout(refetchTimer.current);
			refetchTimer.current = window.setTimeout(() => {
				qc.invalidateQueries({ queryKey: ["overlap-matrix"] });
				qc.invalidateQueries({ queryKey: ["overlap-pair"] });
			}, 5_000);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const notShipped =
		matrix.isError && matrix.error instanceof ApiError && matrix.error.status === 404;

	return (
		<Page
			title="Overlap"
			description="Pairwise partner overlap — shared wallets, clusters, common funding, deposit targets."
			actions={
				<div className="flex items-center gap-2">
					<div className="w-32">
						<Input
							type="number"
							min={0}
							max={100}
							value={minScore}
							onChange={(e) => setMinScore(e.target.value)}
							placeholder="Min score"
							size="sm"
						/>
					</div>
					<Button
						variant="secondary"
						size="sm"
						loading={recompute.isPending}
						onClick={() => recompute.mutate()}
					>
						Recompute
					</Button>
				</div>
			}
		>
			{matrix.isLoading && <div className="text-muted text-sm">Loading…</div>}
			{notShipped && (
				<InfoBanner variant="info" title="Overlap analysis ships in phase 2">
					The partner × partner matrix (shared counterparties, common funders, deposit targets and
					the resulting link score) is computed by the phase-2 overlap job. Until then use the{" "}
					<Link to="/graph" className="text-primary hover:underline">
						graph
					</Link>{" "}
					to inspect shared nodes manually.
				</InfoBanner>
			)}
			{matrix.isError && !notShipped && (
				<InfoBanner variant="danger" title="Could not load overlap matrix">
					{String(matrix.error)}
				</InfoBanner>
			)}
			{matrix.data && (
				<div className="flex gap-3.5 items-start">
					<div className="flex-1 min-w-0">
						<Heatmap data={matrix.data} selected={pair} onSelect={(a, b) => setPair({ a, b })} />
						<SanctionsCard
							sanctions={matrix.data.sanctions ?? []}
							partners={matrix.data.partners}
						/>
					</div>
					{pair && <PairPanel pair={pair} onClose={() => setPair(null)} />}
				</div>
			)}
		</Page>
	);
}

function Heatmap({
	data,
	selected,
	onSelect,
}: {
	data: OverlapMatrix;
	selected: SelectedPair | null;
	onSelect: (a: OverlapPartner, b: OverlapPartner) => void;
}) {
	const { partners, cells } = data;
	const byPair = new Map<string, OverlapCell>();
	for (const c of cells) {
		byPair.set(`${c.a}|${c.b}`, c);
		byPair.set(`${c.b}|${c.a}`, c);
	}
	if (!partners.length)
		return (
			<Card className="mb-3.5">
				<CardBody>
					<div className="text-muted text-sm">No partners with wallets or links yet.</div>
				</CardBody>
			</Card>
		);
	return (
		<Card className="mb-3.5">
			<CardBody>
				<div className="overflow-x-auto">
					<table className="text-xs border-collapse">
						<thead>
							<tr>
								<th className="p-1 text-left text-muted font-normal" />
								{partners.map((p) => (
									<th
										key={p.id}
										className="p-1 text-muted font-normal align-bottom"
										style={{
											writingMode: "vertical-rl",
											transform: "rotate(180deg)",
											maxHeight: 120,
										}}
									>
										{p.name}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{partners.map((row) => (
								<tr key={row.id}>
									<th className="p-1 text-left font-normal whitespace-nowrap">
										<Link to={`/partners/${row.id}`} className="text-primary hover:underline">
											{row.name}
										</Link>
										<span className="text-muted ml-1">({row.walletCount})</span>
									</th>
									{partners.map((col) => {
										if (row.id === col.id)
											return <td key={col.id} className="p-0 bg-surface-2 w-7 h-7" />;
										const c = byPair.get(`${row.id}|${col.id}`);
										const fraudPair =
											!!c &&
											((row.status === "active" && col.status === "terminated_fraud") ||
												(row.status === "terminated_fraud" && col.status === "active"));
										const isSelected =
											!!selected &&
											((selected.a.id === row.id && selected.b.id === col.id) ||
												(selected.a.id === col.id && selected.b.id === row.id));
										const alpha = c ? 0.1 + 0.75 * (Math.min(c.score, 100) / 100) : 0;
										const title = c
											? `${row.name} × ${col.name}: score ${Number(c.score).toFixed(0)} · ${c.count} link(s) · ${c.kinds.join(", ")}${fraudPair ? " · ⚠ active × terminated_fraud" : ""}`
											: `${row.name} × ${col.name}: no overlap`;
										return (
											<td key={col.id} className="p-0 w-7 h-7 border border-line text-center">
												{c ? (
													<button
														type="button"
														className="block w-7 h-7 text-center cursor-pointer"
														style={{
															background: fraudPair
																? `rgba(220, 38, 38, ${alpha})`
																: `rgba(79, 70, 229, ${alpha})`,
															boxShadow: fraudPair
																? "inset 0 0 0 2px #dc2626"
																: isSelected
																	? "inset 0 0 0 2px #111827"
																	: undefined,
														}}
														title={title}
														onClick={() => onSelect(row, col)}
													>
														{c.count}
													</button>
												) : (
													<span className="block w-7 h-7" title={title} />
												)}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="mt-2 text-[11px] text-muted flex flex-wrap items-center gap-x-3 gap-y-1">
					<span>Cell = link count · intensity = max score.</span>
					<span className="inline-flex items-center gap-1">
						<span
							className="inline-block w-3 h-3"
							style={{ boxShadow: "inset 0 0 0 2px #dc2626" }}
						/>
						active × terminated (fraud)
					</span>
					<span>Click a cell to inspect the pair's links.</span>
					<span className="inline-flex items-center gap-1">
						<span className="inline-block w-2.5 h-2.5 rounded-pill border border-dotted border-current" />
						dotted = off-chain identity tie (contact / website / email / name)
					</span>
					<span>direct flow = one partner pays into another's own wallet.</span>
				</div>
			</CardBody>
		</Card>
	);
}

function SanctionsCard({
	sanctions,
	partners,
}: {
	sanctions: OverlapSanction[];
	partners: OverlapPartner[];
}) {
	const nameById = new Map(partners.map((p) => [p.id, p.name]));
	const cols: TableColumn<OverlapSanction>[] = [
		{
			id: "partner",
			header: "Partner",
			accessor: (r) => (
				<Link to={`/partners/${r.partnerId}`} className="text-primary hover:underline">
					{nameById.get(r.partnerId) ?? r.partnerId.slice(0, 8)}
				</Link>
			),
		},
		{ id: "chain", header: "Chain", accessor: (r) => <ChainBadge chain={r.chain} /> },
		{
			id: "address",
			header: "SDN address",
			accessor: (r) => <AddressLink id={r.addressId} address={r.address} />,
		},
		{
			id: "hops",
			header: "Hops",
			accessor: (r) => (
				<Badge size="sm" variant={r.hops <= 1 ? "error" : "warning"}>
					{r.hops} hop{r.hops === 1 ? "" : "s"}
				</Badge>
			),
		},
		{
			id: "score",
			header: "Score",
			accessor: (r) => (
				<span className={r.hops <= 1 ? "text-danger font-semibold" : "text-content"}>
					{Number(r.score).toFixed(0)}
				</span>
			),
		},
	];
	return (
		<Card>
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-3">Sanctioned proximity</h2>
				<div className="overflow-x-auto">
					<Table
						columns={cols}
						data={sanctions}
						keyExtractor={(r) => `${r.partnerId}:${r.addressId}`}
						emptyMessage="No partner within 2 hops of a sanctioned address."
					/>
				</div>
			</CardBody>
		</Card>
	);
}

function PairPanel({ pair, onClose }: { pair: SelectedPair; onClose: () => void }) {
	const qc = useQueryClient();
	const toast = useToast();
	const [dismissLink, setDismissLink] = useState<OverlapLink | null>(null);
	const [note, setNote] = useState("");

	const links = useQuery({
		queryKey: ["overlap-pair", pair.a.id, pair.b.id],
		queryFn: () => api.get<OverlapPairData>(`/api/overlap/pairs/${pair.a.id}/${pair.b.id}`),
	});

	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["overlap-pair", pair.a.id, pair.b.id] });
		qc.invalidateQueries({ queryKey: ["overlap-matrix"] });
	};

	const dismiss = useMutation({
		mutationFn: (p: { id: string; note?: string }) =>
			api.post(`/api/overlap/links/${p.id}/dismiss`, { note: p.note || undefined }),
		onSuccess: () => {
			toast.addToast("success", "Link dismissed");
			setDismissLink(null);
			setNote("");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const restore = useMutation({
		mutationFn: (id: string) => api.post(`/api/overlap/links/${id}/restore`),
		onSuccess: () => {
			toast.addToast("success", "Link restored");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	return (
		<div className="w-96 shrink-0 overflow-auto rounded-md border border-line bg-surface p-3">
			<div className="flex items-start justify-between gap-2 mb-2">
				<div>
					<div className="text-[11px] uppercase text-muted">Pair</div>
					<div className="text-sm text-content font-medium">
						<Link to={`/partners/${pair.a.id}`} className="text-primary hover:underline">
							{pair.a.name}
						</Link>{" "}
						×{" "}
						<Link to={`/partners/${pair.b.id}`} className="text-primary hover:underline">
							{pair.b.name}
						</Link>
					</div>
				</div>
				<button
					type="button"
					onClick={onClose}
					className="text-muted hover:text-content"
					aria-label="Close pair panel"
				>
					<X size={16} />
				</button>
			</div>

			{links.isLoading && <div className="text-muted text-sm">Loading…</div>}
			{links.isError && <div className="text-danger text-sm">{String(links.error)}</div>}
			{links.data && !links.data.links.length && (
				<div className="text-muted text-sm">No links between these partners.</div>
			)}
			{(links.data?.links ?? []).map((l) => (
				<div
					key={l.id}
					className={`rounded-md border border-line p-2 mb-2 ${l.dismissedAt ? "opacity-60" : ""}`}
				>
					<div className="flex items-center justify-between gap-2 mb-1">
						<span className={l.dismissedAt ? "line-through" : ""}>
							<KindBadge kind={l.kind} />
						</span>
						<span className="text-xs text-content font-semibold">{Number(l.score).toFixed(0)}</span>
					</div>
					<dl className="text-xs grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
						{l.usd != null && (
							<>
								<dt className="text-muted">usd</dt>
								<dd className="text-content">{fmtUsd(l.usd)}</dd>
							</>
						)}
						{(l.hopsA != null || l.hopsB != null) && (
							<>
								<dt className="text-muted">hops</dt>
								<dd className="text-content">
									{l.hopsA ?? "—"} / {l.hopsB ?? "—"}
								</dd>
							</>
						)}
						<dt className="text-muted">seen</dt>
						<dd className="text-content">
							{fmtAgo(l.firstSeen)} → {fmtAgo(l.lastSeen)}
						</dd>
					</dl>
					{l.viaAddress && (
						<div className="mt-1.5">
							<div className="text-[11px] uppercase text-muted">via</div>
							<div className="flex items-center gap-1 flex-wrap">
								<ChainBadge chain={l.viaAddress.chain} />
								<AddressLink
									id={l.viaAddress.id}
									address={l.viaAddress.address}
									display={l.viaAddress.display}
								/>
								{l.viaAddress.isSupernode && (
									<Badge size="sm" variant="info">
										supernode
									</Badge>
								)}
							</div>
							{l.viaAddress.labels.length > 0 && (
								<div className="flex flex-wrap gap-1 mt-0.5">
									{l.viaAddress.labels.map((lab) => (
										<Badge key={lab} size="sm" variant="warning">
											{lab}
										</Badge>
									))}
								</div>
							)}
						</div>
					)}
					{l.viaCluster && (
						<div className="mt-1.5 text-xs">
							<span className="text-[11px] uppercase text-muted mr-1.5">via</span>
							<Link to={`/clusters/${l.viaCluster.id}`} className="text-primary hover:underline">
								Cluster · {l.viaCluster.size} addrs
							</Link>
						</div>
					)}
					{l.dismissedAt && (
						<div className="text-[11px] text-muted mt-1">
							dismissed {fmtAgo(l.dismissedAt)}
							{l.dismissNote ? ` — ${l.dismissNote}` : ""}
						</div>
					)}
					<div className="mt-1.5">
						{l.dismissedAt ? (
							<Button
								size="sm"
								variant="outline"
								loading={restore.isPending}
								onClick={() => restore.mutate(l.id)}
							>
								Restore
							</Button>
						) : (
							<Button size="sm" variant="ghost" onClick={() => setDismissLink(l)}>
								Dismiss
							</Button>
						)}
					</div>
				</div>
			))}

			<Modal
				open={!!dismissLink}
				onClose={() => setDismissLink(null)}
				title="Dismiss link?"
				footer={
					<div className="flex justify-end gap-2">
						<Button variant="ghost" onClick={() => setDismissLink(null)}>
							Cancel
						</Button>
						<Button
							variant="danger"
							loading={dismiss.isPending}
							onClick={() => dismissLink && dismiss.mutate({ id: dismissLink.id, note })}
						>
							Dismiss
						</Button>
					</div>
				}
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm text-content">
						Marks this <strong>{dismissLink?.kind.replaceAll("_", " ")}</strong> link between{" "}
						<strong>{pair.a.name}</strong> and <strong>{pair.b.name}</strong> as a false positive.
						It is excluded from the matrix and from link scores until restored.
					</p>
					<Input
						label="Note"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder="Why is this a false positive? (optional)"
					/>
				</div>
			</Modal>
		</div>
	);
}
